import type { CSSProperties, ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import { cx } from '@/lib/cx';
import type { Delta } from '@/lib/format';
import { fmtInt } from '@/lib/format';
import { useCustomers, useParam } from '@/lib/params';
import type { Pill } from '@/lib/types';

import { Sparkline } from './charts';

export const LOGIN_NEEDED = 'Available after login';

export function PageHead({
  title,
  subtitle,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="ph">
      <div>
        <div className="h1">{title}</div>
        {subtitle && <div className="muted">{subtitle}</div>}
      </div>
      {children && <div className="filters">{children}</div>}
    </div>
  );
}

export function Card({
  title,
  right,
  children,
  className,
  style,
}: {
  title?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={cx('card', className)} style={style}>
      {(title || right) && (
        <div className="ch">
          <span className="ct">{title}</span>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function Seg<T extends string>({
  options,
  value,
  onChange,
  compact,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  compact?: boolean;
}) {
  return (
    <span className="seg">
      {options.map((o) => (
        <span
          key={o.value}
          role="button"
          tabIndex={0}
          className={cx(o.value === value && 'on')}
          style={compact ? { padding: '2px 8px' } : undefined}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => e.key === 'Enter' && onChange(o.value)}
        >
          {o.label}
        </span>
      ))}
    </span>
  );
}

export function Toggle({ on, label, onChange }: { on: boolean; label: string; onChange: (on: boolean) => void }) {
  return (
    <span
      className={cx('tog', !on && 'off')}
      role="switch"
      aria-checked={on}
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      onClick={() => onChange(!on)}
      onKeyDown={(e) => e.key === 'Enter' && onChange(!on)}
    >
      <b />
      {label}
    </span>
  );
}

export function Select({
  value,
  options,
  onChange,
  active,
  style,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  active?: boolean;
  style?: CSSProperties;
}) {
  return (
    <label className={cx('sel', active && 'set')} style={style}>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PillTag({ pill }: { pill: Pill }) {
  return <span className={cx('pill', pill.tone)}>{pill.label}</span>;
}

export function DeltaText({ delta, compare }: { delta: Delta | null; compare?: string }) {
  if (!delta) return <span className="d muted">{compare}</span>;
  return (
    <span className={cx('d', delta.tone)}>
      {delta.text}
      {compare && (
        <span className="faint" style={{ fontWeight: 500 }}>
          {' '}
          {compare}
        </span>
      )}
    </span>
  );
}

export function Kpi({
  label,
  value,
  delta,
  compare,
  spark,
  sparkBad,
  footer,
  onClick,
}: {
  label: ReactNode;
  value: ReactNode;
  delta?: Delta | null;
  compare?: string;
  spark?: number[];
  sparkBad?: boolean;
  footer?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <div className="card kpi" role={onClick ? 'button' : undefined} onClick={onClick}>
      <div className="l">{label}</div>
      <div className="n">{value}</div>
      <div className="f">
        {footer ?? <DeltaText delta={delta ?? null} compare={compare} />}
        {spark && <Sparkline values={spark} bad={sparkBad} />}
      </div>
    </div>
  );
}

export function Empty({
  icon,
  title,
  children,
  actions,
}: {
  icon: string;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="ic">{icon}</div>
      <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{title}</div>
      {children && <div>{children}</div>}
      {actions && <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>{actions}</div>}
    </div>
  );
}

export function NotSynced({ what }: { what: string }) {
  return (
    <Empty icon="◌" title={`${what}: not synced yet`}>
      This needs data the sync doesn't copy yet. It arrives with the next sync phase.
    </Empty>
  );
}

export function AiOff({ what }: { what: string }) {
  return (
    <Empty icon="✦" title={`${what} needs AI analysis`}>
      AI analysis is off in this region (USAGE_AI_ENABLED).
    </Empty>
  );
}

export function ErrorCard({ error }: { error: Error }) {
  return (
    <div className="card">
      <Empty icon="!" title="Couldn't load this">
        {error.message}
      </Empty>
    </div>
  );
}

export function SkeletonCard({ height = 110 }: { height?: number }) {
  return (
    <div className="card">
      <div className="skel" style={{ height: 10, width: '40%', marginBottom: 12 }} />
      <div className="skel" style={{ height }} />
    </div>
  );
}

export function Loaded<T>({
  query,
  children,
  skeleton,
}: {
  query: UseQueryResult<T, Error>;
  children: (data: T) => ReactNode;
  skeleton?: ReactNode;
}) {
  if (query.error) return <ErrorCard error={query.error} />;
  if (query.data === undefined) return <>{skeleton ?? <SkeletonCard />}</>;
  return <>{children(query.data)}</>;
}

export function HBars({
  rows,
  bad,
  format = fmtInt,
  onClick,
}: {
  rows: { key: string; label: string; value: number }[];
  bad?: boolean;
  format?: (value: number) => string;
  onClick?: (key: string) => void;
}) {
  const max = Math.max(1e-9, ...rows.map((r) => r.value));
  return (
    <div>
      {rows.map((r) => (
        <div
          key={r.key}
          className={cx('hbar', onClick && 'clickable')}
          onClick={onClick ? () => onClick(r.key) : undefined}
        >
          <span className="t" title={r.label}>
            {r.label}
          </span>
          <span className={cx('b', bad && 'bad')} style={{ width: `${(r.value / max) * 55}%` }} />
          <span className="v">{format(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function StackBar({
  parts,
  width,
  height,
}: {
  parts: { key: string; share: number; color: string; label: string }[];
  width?: number | string;
  height?: number;
}) {
  return (
    <span className="stack" style={{ width, height }}>
      {parts
        .filter((p) => p.share > 0)
        .map((p) => (
          <i key={p.key} title={p.label} style={{ width: `${p.share * 100}%`, background: p.color }} />
        ))}
    </span>
  );
}

export function Bullet({ share, pace, width }: { share: number; pace: number; width?: number }) {
  const tone = share >= 1 ? 'x' : share >= 0.8 ? 'w' : undefined;
  return (
    <span className="bullet" style={width ? { width } : undefined}>
      <b className={tone} style={{ width: `${Math.min(1, share) * 100}%` }} />
      <s style={{ left: `${Math.min(1, pace) * 100}%` }} />
    </span>
  );
}

export function AreasDots({ used, all }: { used: string[]; all: string[] }) {
  return (
    <span className="areas" title={used.join(', ')}>
      {all.map((a) => (
        <i key={a} className={cx(used.includes(a) && 'u')} />
      ))}
    </span>
  );
}

export function Disabled({ children, primary }: { children: ReactNode; primary?: boolean }) {
  return (
    <button type="button" className={cx('btn', primary && 'pri')} disabled title={LOGIN_NEEDED}>
      {children}
    </button>
  );
}

export function RangeSeg({ options, fallback }: { options: { value: string; label: string }[]; fallback: string }) {
  const [range, setRange] = useParam('range', fallback);
  return <Seg options={options} value={range} onChange={setRange} />;
}

export function CustomersToggle() {
  const [customers, setCustomers] = useCustomers();
  return <Toggle on={customers} label="Customers only" onChange={setCustomers} />;
}
